// utility methods for workshop status display and modal behavior
import { Workshop, WorkshopSessionStatus, WorkshopMode } from '@core/models/workshop';

// These Workshop Statuses take precedence over statuses related to seating availability.
const WORKSHOP_PRIORITY_STATUSES: WorkshopSessionStatus[] = ['Canceled', 'Postponed', 'Completed', 'InProgress'];

// Configure threshold for last seats as needed or if DB value replace this
const LAST_SEATS_THRESHOLD = 5;

// Statuses to display to users
export type FriendlyStatus = 'Cancelled' | 'Postponed' | 'Full' | 'Last Seats' | 'Open' | 'In Progress' | 'Completed';


// Return a user friendly status to use with workshops
export function getFriendlyStatus(workshop: Workshop): FriendlyStatus {
  if (WORKSHOP_PRIORITY_STATUSES.includes(workshop.sessionStatus)) {
    return workshop.sessionStatus === 'InProgress' ? 'In Progress' : workshop.sessionStatus as FriendlyStatus;
  }
  if (workshop.seatsRemaining === 0) return 'Full';
  if ((workshop.seatsRemaining ?? 0) <= LAST_SEATS_THRESHOLD && !workshop.isRegistered) return 'Last Seats'; 
  return 'Open';
}

// Map statuses to css rules
export function getWorkshopStatusClass(workshop: Workshop): string {
  switch (getFriendlyStatus(workshop)) {
    case 'Open':       return 'status-open';
    case 'Last Seats': return 'status-last-seats';
    case 'In Progress':
    case 'Full':
    case 'Completed':
    case 'Postponed':
    case 'Cancelled':  return 'status-cancelled';
    default:           return '';
  }
}
//
export function getRegisteredWorkshopStatusClass(workshop: Workshop): string {
  switch (getFriendlyStatus(workshop)) {
    case 'Open':       return 'status-open';
    case 'Last Seats': return 'status-open';
    case 'In Progress':return 'status-inprogress';
    case 'Full':
    case 'Completed':
    case 'Postponed':
    case 'Cancelled':  return 'status-cancelled';
    default:           return '';
  }
}

// Map statuses to css rules related to seating availability
export function getWorkshopSeatBadgeClass(workshop: Workshop): string {
  switch (getFriendlyStatus(workshop)) {
    case 'Open':       return 'bg-success-dark';
    case 'Last Seats': return 'bg-alert-dark';
    default:           return 'd-none';
  }
}

export function shouldShowSeats(workshop: Workshop): boolean {
  const status = getFriendlyStatus(workshop);
  return status === 'Open' || status === 'Last Seats';
}


export function getWorkshopModalMode(workshop: Workshop): 'register' | 'registered' | 'full' | 'cancelled' {
  const sessionStatus = workshop.sessionStatus.toLowerCase();
  if (sessionStatus === 'canceled' || sessionStatus === 'postponed' || sessionStatus === 'inprogress') return 'cancelled';
  if (workshop.isRegistered && sessionStatus != 'completed') return 'registered';
  if (sessionStatus === 'completed' || workshop.seatsRemaining === 0) return 'full';
  return 'register';
}

export function getWorkshopButtonClass(workshop: Workshop): string {
  switch (getWorkshopModalMode(workshop)) {
    case 'registered':           return 'btn btn-secondary btn-floating p-4';
    case 'full':
    case 'cancelled':            return 'btn btn-outline-dark btn-floating p-4';
    default:                     return 'btn btn-primary btn-floating p-4';
  }
}

export function getWorkshopCardButtonClass(workshop: Workshop): string {
  switch (getWorkshopModalMode(workshop)) {
    case 'registered':           return 'btn btn-secondary w-100 mx-3 mb-3';
    case 'full':
    case 'cancelled':            return 'btn btn-outline-dark w-100  mx-3 mb-3';
    default:                     return 'btn btn-primary w-100  mx-3 mb-3';
  }
}

export function getWorkshopButtonIcon(workshop: Workshop): string {
  switch (getWorkshopModalMode(workshop)) {
    case 'registered': return 'fa-solid fa-xl fa-person-walking-dashed-line-arrow-right text-black';
    case 'full':
    case 'cancelled':  return 'fa-regular fa-folder-open fa-xl text-black';
    default:           return 'fa-solid fa-user-plus fa-lg text-white';
  }
}

export function getWorkshopButtonText(workshop: Workshop): string {
  switch (getWorkshopModalMode(workshop)) {
    case 'registered': return 'Cancel Registration';
    case 'full':
    case 'cancelled':  return 'View';
    default:           return 'Register';
  }
}

export function getWorkshopAriaLabel(workshop: Workshop): string {
  switch (getWorkshopModalMode(workshop)) {
    case 'registered': return `Cancel registration for ${workshop.title}`;
    case 'full':       return `View details for ${workshop.title}`;
    case 'cancelled':  return `View cancellation details for ${workshop.title}`;
    default:           return `Register for ${workshop.title}`;
  }
}

// Maps API mode values to display labels
export function formatWorkshopMode(workshop: Workshop): string {
  switch (workshop.mode) {
    case 'InPerson': return 'In Person';
    case 'Video':    return 'Online';
    case 'Hybrid':   return 'Hybrid';
    default:         return workshop.mode;
  }
}

// Display formatted location string for in-person or hybrid workshops.
// Returns null for video-only workshops with no address.
export function formatWorkshopLocation(workshop: Workshop): string | null {
  const parts = [
    workshop.addressLine1,
    workshop.addressLine2,
    workshop.city,
    workshop.state,
    workshop.zipCode
  ].filter((p): p is string => !!p);

  return parts.length > 0 ? parts.join(', ') : null;
}


// #region Date Time 
export function formatWorkshopDateTime(date: Date): string {
  const datePart = date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'America/Los_Angeles'
  });

  const timePart = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'America/Los_Angeles'
  }).toUpperCase();

  return `${datePart} – ${timePart}`;
}

export function formatWorkshopDateOnly(date: Date): string {
  const datePart = date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'America/Los_Angeles'
  });
  return `${datePart}`;
}

export function formatWorkshopTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'America/Los_Angeles'
  }).toUpperCase();
}
//#endregion